import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SignUpModal from '../components/SignupModal';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import ResetPasswordModal from '../components/ResetPasswordModal';
import LoginForm from '../components/auth/LoginForm';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/images/KSeco.png';
import { motion } from 'framer-motion';

const LoginPage = () => {
    const { isAuthenticated, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || '/';

    const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
    const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);

    useEffect(() => {
        if (!loading && isAuthenticated) {
            navigate(from === '/login' ? '/' : from, { replace: true });
        }
    }, [isAuthenticated, loading, navigate, from]);

    const openLoginView = () => {
        setIsSignUpModalOpen(false);
        setIsForgotPasswordModalOpen(false);
        setIsResetPasswordModalOpen(false);
    };

    const openSignUpModal = () => {
        setIsSignUpModalOpen(true);
        setIsForgotPasswordModalOpen(false);
        setIsResetPasswordModalOpen(false);
    };

    const openForgotPasswordModal = () => {
        setIsSignUpModalOpen(false);
        setIsForgotPasswordModalOpen(true);
        setIsResetPasswordModalOpen(false);
    };

    const openResetPasswordModal = () => {
        setIsSignUpModalOpen(false);
        setIsForgotPasswordModalOpen(false);
        setIsResetPasswordModalOpen(true);
    };

    const closeAuxModals = () => {
        openLoginView();
    };

    if (loading) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-[#0f2419]">
                <div className="tw-w-12 tw-h-12 tw-rounded-full tw-border-2 tw-border-white/20 tw-border-t-white tw-animate-spin" />
            </div>
        );
    }

    if (isAuthenticated) {
        return null;
    }

    return (
        <div className="tw-min-h-screen tw-flex tw-flex-col lg:tw-flex-row tw-bg-[#0c1f14]">
            <motion.aside
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
                className="tw-relative tw-flex tw-flex-col tw-justify-between tw-px-8 tw-py-10 lg:tw-w-[46%] xl:tw-w-[42%] tw-min-h-[240px] lg:tw-min-h-screen tw-overflow-hidden"
            >
                <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-primary tw-via-[#2d6b47] tw-to-[#0c1f14]" />

                <div className="tw-relative tw-z-10">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <img
                            src={logo}
                            alt="KSeco KS"
                            className="tw-h-36 lg:tw-h-48 tw-w-auto tw-drop-shadow-lg tw-brightness-0 tw-invert"
                        />
                    </div>

                    <h1 className="tw-mt-10 tw-text-3xl sm:tw-text-4xl tw-font-bold tw-text-white tw-tracking-tight tw-leading-tight">
                        Business data
                        <span className="tw-block tw-text-green-goose/95 tw-mt-1">for Kosovo</span>
                    </h1>

                    <p className="tw-mt-5 tw-text-base tw-text-white/85 tw-max-w-md tw-leading-relaxed">
                        Secure access to the business registry, statistics, and analytics - available only to authorized users.
                    </p>
                </div>

                <p className="tw-relative tw-z-10 tw-text-xs tw-text-white/50 tw-pt-8 lg:tw-pt-0">
                    © {new Date().getFullYear()} KSeco KS
                </p>
            </motion.aside>

            <main className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-px-5 tw-py-12 sm:tw-px-10 tw-bg-gradient-to-b tw-from-[#f7fbf8] tw-to-white">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.06 }}
                    className="tw-w-full tw-max-w-md"
                >
                    <div className="lg:tw-hidden tw-flex tw-justify-center tw-mb-8">
                        <img src={logo} alt="" className="tw-h-16 tw-w-auto" />
                    </div>

                    <div className="tw-rounded-2xl tw-bg-white tw-border tw-border-gray-100 tw-shadow-xl tw-shadow-primary/5 tw-p-8 sm:tw-p-10">
                        <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-tracking-tight">
                            Welcome back
                        </h2>

                        <p className="tw-mt-2 tw-text-sm tw-text-gray-500">
                            Sign in to continue to the business dashboard.
                        </p>

                        <div className="tw-mt-8">
                            <LoginForm
                                onSuccess={() => navigate(from === '/login' ? '/' : from, { replace: true })}
                                onSignUpClick={openSignUpModal}
                                onForgotPasswordClick={openForgotPasswordModal}
                            />
                        </div>
                    </div>
                </motion.div>
            </main>

            <SignUpModal
                isOpen={isSignUpModalOpen}
                onClose={closeAuxModals}
                onLoginClick={openLoginView}
            />
            <ForgotPasswordModal
                isOpen={isForgotPasswordModalOpen}
                onClose={closeAuxModals}
                onLoginClick={openLoginView}
                onResetPasswordClick={openResetPasswordModal}
            />
            <ResetPasswordModal
                isOpen={isResetPasswordModalOpen}
                onClose={closeAuxModals}
                onLoginClick={openLoginView}
            />
        </div>
    );
};

export default LoginPage;